import { isDefined, isNotDefined } from '@sgme/fp';
import { format } from 'date-fns';
import { v4 as getNewGuid } from 'uuid';

import type { OrderError } from '@/store/bulkOrder/types.ts';
import type { ValidationResponseDetailContent } from '@/store/orders/state.ts';
import { toUtc } from '@/utils/date.ts';

import {
  BulkOrderStatus,
  type FixingBulkOrder,
  type FixingOrderError,
  FixingOrderFieldName,
  type FixingOrderRow,
  type StopLossBulkOrder,
  type StopLossOrderError,
  StopLossOrderFieldName,
  type StopLossOrderRow,
} from '../state';

export const loadStopLossOrderXLSX = (
  content: StopLossOrderRow[],
  notificationEmails: string[],
): StopLossBulkOrder[] => {
  return content.map((row) => {
    const { bdrId, currency, currencyPair: rawCurrencyPair, limitPrice, notional, trigger, way } = row;

    const id = getNewGuid();

    const currencyPair = `${rawCurrencyPair.slice(0, 3)}/${rawCurrencyPair.slice(3)}`;
    const allCurrencies = currencyPair.split('/');

    const errors: StopLossOrderError[] = [];

    if (!allCurrencies.includes(currency)) {
      errors.push({
        field: StopLossOrderFieldName.currency,
        message: 'currency not comptible with the currencyPair',
        code: -1,
      });
    }

    return {
      type: 'StopLossOrder',
      properties: {
        id,
        omsId: undefined,
        currencyPair,
        bdrId,
        notional: Number(notional),
        way: way === 'B' ? 'Bid' : 'Ask',
        currency,
        currencyIndex: allCurrencies[0] === currency ? 1 : 2,
        limitPrice: Number(Number(limitPrice).toFixed(5)),
        expiry: isDefined(row.expiryDate)
          ? {
              type: 'ExpiryDate',
              date: format(row.expiryDate, 'yyyy-MM-dd'),
              time: format(toUtc(new Date(row.expiryDate)), 'HH:mm:ss'),
              dateTime: row.expiryDate,
            }
          : { type: 'GoodTillCancelled' },
        trigger,
        notificationEmails,
        status: BulkOrderStatus.IDLE,
        errors,
        mode: 'VIEW',
      },
    } satisfies StopLossBulkOrder;
  });
};

export const loadFixingOrderXLSX = (content: FixingOrderRow[], notificationEmails: string[]): FixingBulkOrder[] => {
  return content.map((row) => {
    const {
      bdrId,
      currency,
      currencyPair: rawCurrencyPair,
      fixingReference,
      notional,
      fixingDate,
      marginType,
      fixingTime,
      fixingPlace,
      priceType,
      commercialMargin,
      deliveryDate,
      way,
    } = row;

    const id = getNewGuid();

    const currencyPair = `${rawCurrencyPair.slice(0, 3)}/${rawCurrencyPair.slice(3)}`;
    const allCurrencies = currencyPair.split('/');

    const errors: FixingOrderError[] = [];

    if (!allCurrencies.includes(currency)) {
      errors.push({
        field: FixingOrderFieldName.currency,
        message: 'currency not comptible with the currencyPair',
        code: -1,
      });
    }

    if (isNotDefined(fixingDate) || fixingDate === '') {
      errors.push({
        field: FixingOrderFieldName.fixingDateUtc,
        message: 'Fixing date is necessary',
        code: -1,
      });
    }

    return {
      type: 'FixingOrder',
      properties: {
        id,
        omsId: undefined,
        currencyPair,
        bdrId,
        way: way === 'B' ? 'Bid' : 'Ask',
        notional: isDefined(notional) ? Number(notional) : 0,
        fixingReference,
        fixingMarginType: marginType,
        fixingPlace: fixingPlace,
        fixingTime: fixingTime,
        fixingPriceType: priceType,
        commercialMargin: isDefined(commercialMargin) ? Number(commercialMargin) : undefined,
        fixingDateUtc: fixingDate,
        deliveryDate,
        currency,
        currencyIndex: allCurrencies[0] === currency ? 1 : 2,
        notificationEmails,
        status: BulkOrderStatus.IDLE,
        errors,
        mode: 'VIEW',
      },
    } satisfies FixingBulkOrder;
  });
};

export const sortByBrdAndCurrencyPair = (
  left: StopLossBulkOrder | FixingBulkOrder,
  right: StopLossBulkOrder | FixingBulkOrder,
) => {
  const leftKey = `${left.properties.bdrId}${left.properties.currencyPair}}`;
  const rightKey = `${right.properties.bdrId}${right.properties.currencyPair}}`;

  if (leftKey < rightKey) {
    return -1;
  }

  if (leftKey > rightKey) {
    return 1;
  }

  return 0;
};

export const sortByCurrencyPair = (left: StopLossBulkOrder, right: StopLossBulkOrder) => {
  if (left.properties.currencyPair < right.properties.currencyPair) {
    return -1;
  }

  if (left.properties.currencyPair > right.properties.currencyPair) {
    return 1;
  }

  return 0;
};

export const extractErrorsFromValidation = (
  validationResponseDetailContent: ValidationResponseDetailContent[],
): OrderError[] => {
  return validationResponseDetailContent.map(
    (details: { field: string; errorDescription: string; errorCode: number }) =>
      ({
        field: details.field as StopLossOrderFieldName,
        message: details.errorDescription,
        code: details.errorCode,
      }) satisfies OrderError,
  );
};

export const mapWithKey = <TValue, TOutput>(
  record: Record<string, TValue>,
  mapFn: (value: TValue) => TOutput,
): Record<string, TOutput> => {
  return Object.entries(record).reduce(
    (acc, [key, value]) => {
      acc[key] = mapFn(value);

      return acc;
    },
    {} as Record<string, TOutput>,
  );
};
